import { useContext, useState } from 'react';
import {
  QueryClient,
  QueryClientContext,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Modal, ModalFooter } from '@/components/common';
import api, { getErrorMessage } from '@/services/api';
import { claimsDeskService } from '@/services/claimsDesk';
import type { ClaimFilters } from '@/types';

/**
 * #401: the insurer Claims Desk queue -- the tabs above the claims list and
 * the insurer admin's approval-limit setting. Counts come from
 * `/claims/statistics/`, which the server scopes to the insurer's forwarded
 * claims.
 */

export interface InsurerQueueTab {
  key: string;
  label: string;
  filters: Partial<ClaimFilters>;
  statuses?: string[];
}

// eslint-disable-next-line react-refresh/only-export-components
export const INSURER_QUEUE_TABS: InsurerQueueTab[] = [
  { key: 'all', label: 'All', filters: {} },
  { key: 'awaiting', label: 'Awaiting my decision', filters: { awaiting_my_decision: true } },
  {
    key: 'documents_pending',
    label: 'Documents pending',
    filters: { status: 'documents_pending' },
    statuses: ['documents_pending'],
  },
  {
    key: 'under_review',
    label: 'Under review',
    filters: { status: 'under_review' },
    statuses: ['under_review'],
  },
  {
    key: 'settlement',
    label: 'Approved / Settlement',
    filters: { status_in: 'approved,partially_approved,settlement_pending' },
    statuses: ['approved', 'partially_approved', 'settlement_pending'],
  },
  {
    key: 'closed',
    label: 'Closed',
    filters: { status_in: 'rejected,settled,closed' },
    statuses: ['rejected', 'settled', 'closed'],
  },
];

interface Statistics {
  summary: { total_count: number; awaiting_my_decision?: number };
  by_status: { status: string; count: number }[];
}

function tabCount(tab: InsurerQueueTab, stats: Statistics | undefined): number | null {
  if (!stats) return null;
  if (tab.key === 'all') return stats.summary.total_count;
  if (tab.key === 'awaiting') return stats.summary.awaiting_my_decision ?? 0;
  return stats.by_status
    .filter((row) => tab.statuses?.includes(row.status))
    .reduce((sum, row) => sum + row.count, 0);
}

let fallbackClient: QueryClient | null = null;
/** Same seam as `useInsurerPermissions`: an isolated list render has no provider. */
function useResolvedQueryClient(): QueryClient {
  const contextClient = useContext(QueryClientContext);
  if (contextClient) return contextClient;
  if (!fallbackClient) fallbackClient = new QueryClient();
  return fallbackClient;
}

export function InsurerQueueTabs({
  active,
  onChange,
}: {
  active: string;
  onChange: (key: string) => void;
}) {
  const client = useResolvedQueryClient();
  const { data: stats } = useQuery(
    {
      queryKey: ['claims', 'insurer-statistics'],
      queryFn: async () => (await api.get<Statistics>('/claims/statistics/')).data,
    },
    client
  );
  return (
    <div role="tablist" aria-label="Claims queue" className="mb-4 flex flex-wrap gap-2">
      {INSURER_QUEUE_TABS.map((tab) => {
        const count = tabCount(tab, stats);
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.key)}
            className={`rounded-full px-3 py-1 text-sm ${
              selected
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {tab.label}
            {count !== null && <span className="ml-1.5 font-semibold">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Insurer admin only: the claims approval limit (fail-closed when blank). */
export function ClaimsLimitButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        Approval limit
      </Button>
      {open && <ClaimsLimitModal onClose={() => setOpen(false)} />}
    </>
  );
}

function ClaimsLimitModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['claims-desk-settings'],
    queryFn: claimsDeskService.settings,
  });
  const [value, setValue] = useState<string | null>(null);
  const current = value ?? data?.claims_approval_limit ?? '';
  const mutation = useMutation({
    mutationFn: () => claimsDeskService.updateLimit(current.trim() === '' ? null : current.trim()),
    onSuccess: () => {
      toast.success('Claims approval limit saved');
      queryClient.invalidateQueries({ queryKey: ['claims-desk-settings'] });
      queryClient.invalidateQueries({ queryKey: ['claim'] });
      onClose();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const invalid = current.trim() !== '' && !(Number(current) >= 0);
  return (
    <Modal isOpen onClose={onClose} title="Claims approval limit">
      <div className="space-y-2">
        <label
          htmlFor="claims-approval-limit"
          className="block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          Claims approval limit (INR)
        </label>
        <input
          id="claims-approval-limit"
          type="number"
          min="0"
          step="0.01"
          disabled={isLoading || !data?.can_change}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          value={current}
          onChange={(e) => setValue(e.target.value)}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The largest amount a team member with Approve / Settle may approve, settle (net paid) or
          reject (claimed amount). Above it only you can decide. Left blank, only you can decide any
          claim.
        </p>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          isLoading={mutation.isPending}
          disabled={invalid || !data?.can_change}
        >
          Save limit
        </Button>
      </ModalFooter>
    </Modal>
  );
}
